import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Spinner,
  Label,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, {
} from "../../../../Components/Common/CommonForm";
import {
  loadCodelistsDetailId,
  REASON_FOR_REPRINT_OPTIONS,
} from "../../../../api/modules/common.option";

import {
  showToastMessage,
} from "../../../../common/service/CommonService";
import { queryClient } from "api/queryClient";
import { formatDateTimeLocal } from "helpers/constant";
import { useDispensingLabelService } from "modules/PrintLabel/DispensingLabel/api/useDispensingLabelService";
import { useSubjectIdcardService } from "modules/PrintLabel/ICardLabel/api/useSubjectIdcardService";
import { useVacutainerLabelService } from "modules/PrintLabel/VaccutainerLabel/api/useVacutainerLabelService";
import { useRiaLabelService } from "modules/PrintLabel/RiaLabel/api/useRiaLabelService";
import { useBoxLabelService } from "modules/PrintLabel/BoxLabel/api/useBoxLabelService";
import { rePrintFields } from "../helper/reprint.constant";
import { ReprintForm, RePrintModelProps } from "../helper/reprint.types";
import { useSubjectLabelService } from "modules/PrintLabel/SubjectLabel/api/useSubjectLabelService";

/* ─────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────── */

const RePrintModal: React.FC<RePrintModelProps> = ({
  isOpen,
  record,
  onConfirm,
  onClose,
  isLoading = false,
  selectedCount,
  selectedRecords,
}) => {
  const { InsertReprintHistoryMutation: SubjectLabelMutation } =
    useSubjectLabelService();
  const { InsertReprintHistoryMutation: IdcardMutation } =
    useSubjectIdcardService();
  const { InsertReprintHistoryMutation: CryovialMutation } =
    useRiaLabelService({});
  const { InsertReprintHistoryMutation: BoxLabelMutation } =
    useBoxLabelService({});
  const { InsertReprintHistoryMutation: VacutainerMutation } =
    useVacutainerLabelService();
  const { InsertDispensingReprintHistoryMutation: DispensingMutation } =
    useDispensingLabelService({ studyid: null, subjectno: null });
  const [printCount, setPrintCount] = useState(1);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ReprintForm>({
    defaultValues: {
      reason: "",
      comments: "",
    },
  });

  useEffect(() => {
    loadCodelistsDetailId(["REASON_FOR_REPRINT_OPTIONS"]);
  }, []);

  useEffect(() => {
    if (isOpen) {
      reset({
        reason: "",
        comments: "",
      });
      setPrintCount(1);
    }
  }, [isOpen, record]); // Added record to dependency array

  if (!isOpen || !record) return null;

  const handleConfirm = (values: ReprintForm) => {
    if (!record) return;

    const records =
      selectedRecords && selectedRecords.length > 0
        ? selectedRecords
        : [record];

    const basePayload = {
      studyid: record.studyid,
      reasonforreprint: values.reason, // sent as integer (parsed in label.api.ts)
      additionalcomments: values.comments,
      printcount: printCount,
    };

    if (record.labeltype === "Subject Label") {
      const payload = {
        ...basePayload,
        jsondata: records.map((r) => ({
          volunteerassignmentid: r.volunteerassignmentid,
          subjectno: r.subjectno,
          sampleseparationid:
            r.sampleseparationid || r.volunteerassignmentid || 0,
        })),
      };
      SubjectLabelMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Reprint history saved successfully!",
          );
          queryClient.invalidateQueries({
            queryKey: ["reprint-label-SelectAll"],
          });
          onConfirm(values);
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to save reprint history",
          );
        },
      });
    } else if (record.labeltype === "IdCard Label") {
      const payload = {
        ...basePayload,
        jsondata: records.map((r) => ({
          volunteerassignmentid: r.volunteerassignmentid,
          subjectno: r.subjectno,
        })),
      };
      IdcardMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "ID Card reprint history saved successfully!",
          );
          queryClient.invalidateQueries({
            queryKey: ["reprint-label-SelectAll"],
          });
          onConfirm(values);
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to save ID Card reprint history",
          );
        },
      });
    } else if (
      record.labeltype === "Cryovial Label" ||
      record.labeltype === "Cryovial/Riavial Label"
    ) {
      const payload = {
        ...basePayload,
        jsondata: records.map((r) => ({
          sampleseparationid: r.sampleseparationid || r.volunteerassignmentid || 0,
          subjectno: r.subjectno,
        })),
      };
      CryovialMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Cryovial Label reprint history saved successfully!",
          );
          queryClient.invalidateQueries({
            queryKey: ["reprint-label-SelectAll"],
          });
          onConfirm(values);
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to save Cryovial Label reprint history",
          );
        },
      });
    } else if (record.labeltype === "Box Label") {
      const payload = {
        ...basePayload,
        jsondata: records.map((r: any) => ({
          subjectboxid: r.volunteerassignmentid,
          subjectno: r.subjectno,
        })),
      };
      BoxLabelMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Box Label reprint history saved successfully!",
          );
          queryClient.invalidateQueries({
            queryKey: ["reprint-label-SelectAll"],
          });
          onConfirm(values);
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to save Box Label reprint history",
          );
        },
      });
    } else if (record.labeltype === "Vacutainer Label") {
      const payload = {
        ...basePayload,
        jsondata: records.map((r) => ({
          samplecollectionid: r.samplecollectionid || r.volunteerassignmentid, // Handle fallback
          subjectno: r.subjectno,
        })),
      };
      VacutainerMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Vacutainer reprint history saved successfully!",
          );
          // You might want to invalidate vacutainer queries too, matching existing patterns
          queryClient.invalidateQueries({
            queryKey: ["vacutainer-label-selectAll"],
          });
          queryClient.invalidateQueries({
            queryKey: ["reprint-label-SelectAll"],
          });
          onConfirm(values);
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to save Vacutainer reprint history",
          );
        },
      });
    } else if (record.labeltype === "Dispensing Label") {
      const payload = {
        ...basePayload,
        studyid: record.studyid,
        dispensingid: record.dispensingid || record.volunteerassignmentid || null,
        subjectno: record.subjectno,
        jsondata: records.map((r: any) => ({
          dispensingid: r.dispensingid || r.volunteerassignmentid || null,
          subjectno: r.subjectno,
        })),
      };
      DispensingMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Dispensing Label reprint history saved successfully!",
          );
          queryClient.invalidateQueries({
            queryKey: ["reprint-label-SelectAll"],
          });
          onConfirm(values);
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to save Dispensing Label reprint history",
          );
        },
      });
    } else {
      onConfirm(values);
    }
  };

  const isSubmitting =
    isLoading ||
    SubjectLabelMutation.isPending ||
    IdcardMutation.isPending ||
    CryovialMutation.isPending ||
    BoxLabelMutation.isPending ||
    VacutainerMutation.isPending ||
    DispensingMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="md"
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={onClose} className="p-3">
        <i className="ri-printer-line me-2 align-middle" />
        {selectedCount && selectedCount > 1
          ? `Reprint Label (${selectedCount} Selected)`
          : `Reprint Label ( ${record.subjectno} )`
        }
      </ModalHeader>

      <ModalBody>

        {/* ── Info Card (hidden for multi-select reprint) ── */}
        {(!selectedCount || selectedCount <= 1) && (
          <div className="bg-light rounded p-3 mb-3">
            <Row className="g-2">
              <Col xs={6}>
                <p className="text-uppercase fw-bold text-muted mb-1" style={{ fontSize: 9, letterSpacing: "0.6px" }}>
                  Print ID
                </p>
                <p className="fw-semibold text-dark mb-0 small">{record.printid}</p>
              </Col>
              <Col xs={6}>
                <p className="text-uppercase fw-bold text-muted mb-1" style={{ fontSize: 9, letterSpacing: "0.6px" }}>
                  Label Type
                </p>
                <p className="fw-semibold text-dark mb-0 small">{record.labeltype}</p>
              </Col>
              <Col xs={6}>
                <p className="text-uppercase fw-bold text-muted mb-1 mt-2" style={{ fontSize: 9, letterSpacing: "0.6px" }}>
                  Subject / Study
                </p>
                <p className="fw-semibold text-dark mb-0 small">{record.subjectno} / {record.studycode}</p>
              </Col>
              <Col xs={6}>
                <p className="text-uppercase fw-bold text-muted mb-1 mt-2" style={{ fontSize: 9, letterSpacing: "0.6px" }}>
                  Original Time
                </p>
                <p className="fw-semibold text-dark mb-0 small">{record.printdate.includes('T') ? formatDateTimeLocal(record.printdate) : ""}</p>
              </Col>
            </Row>
          </div>
        )}


        {/* ── Quantity Selector (Exact Match) ── */}
        {record.labeltype !== "IdCard Label" && (
          <div className="mb-3">
            <Label className="form-label text-dark fw-semibold mb-1">
              Quantity
            </Label>
            <div
              className="d-flex align-items-center justify-content-between border rounded-2 bg-white px-2"
              style={{ width: "100px", height: "34px" }}
            >
              <Button
                size="sm"
                color="link"
                className="text-muted p-0 shadow-none"
                onClick={() => setPrintCount((prev) => Math.max(1, prev - 1))}
                disabled={printCount <= 1}
              >
                <i className="ri-subtract-line fs-6" />
              </Button>
              <span className="fw-bold text-dark fs-6">{printCount}</span>
              <Button
                size="sm"
                color="link"
                className="text-muted p-0 shadow-none"
                onClick={() => setPrintCount((prev) => prev + 1)}
              >
                <i className="ri-add-line fs-6" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Form Fields ── */}
        <CommonForm
          fields={rePrintFields(REASON_FOR_REPRINT_OPTIONS)}
          control={control}
          errors={errors}
          columns={1}
          setValue={setValue}
          getValues={getValues}
        />
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="dark" outline onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          outline
          onClick={handleSubmit(handleConfirm)}
          disabled={isSubmitting}
        >
          {isSubmitting && <Spinner size="sm" className="me-2" />}
          Confirm Reprint
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RePrintModal;
